import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Avatar, Alert, Button, Tooltip,
  LinearProgress, Divider
} from '@mui/material';
import { Print, Download, Refresh, Info } from '@mui/icons-material';
import { scoresheetAPI, settingsAPI, teachersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Grade colour helper
const gradeBg = (g) => {
  if (!g || g === '-') return 'transparent';
  if (g === 'A*') return '#1a5c2e';
  if (g === 'A')  return '#1a7a3c';
  if (g === 'B')  return '#1565c0';
  if (g === 'C')  return '#2e7d32';
  if (g === 'D')  return '#b7770d';
  if (g === 'E')  return '#e67e22';
  if (g === 'F')  return '#c0392b';
  return '#888';
};

// Subject cell — shows percentage + coloured grade pill
const SubjectCell = ({ data, passMark = 50 }) => {
  if (!data || data.percent === null) {
    return <TableCell align="center" sx={{ p: '3px 4px', color: '#bbb', fontSize: 11 }}>—</TableCell>;
  }
  const pct = data.percent;
  const isFail = pct < passMark;
  return (
    <TableCell align="center" sx={{ p: '3px 4px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
        <Typography
          fontSize={12} fontWeight={700}
          color={isFail ? '#c0392b' : '#1a1a2e'}>
          {pct.toFixed(1)}
        </Typography>
        {data.grade !== '-' && (
          <Box sx={{
            bgcolor: gradeBg(data.grade),
            color: 'white', borderRadius: 0.8,
            px: 0.6, fontSize: 8, fontWeight: 800, lineHeight: 1.5
          }}>
            {data.grade}
          </Box>
        )}
      </Box>
    </TableCell>
  );
};

export default function Scoresheet() {
  const [academicYears, setAcademicYears] = useState([]);
  const { user } = useAuth();
  const isClassTeacher = user?.role === 'class_teacher';
  const [classes, setClasses] = useState([]);
  const [homeClasses, setHomeClasses] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [scoresheet, setScoresheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => {
      if (r.data.success) {
        setAcademicYears(r.data.data);
        const cur = r.data.data.find(y => y.is_current);
        if (cur) setSelectedYear(cur.id);
      }
    }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    if (user?.teacher_id) {
      teachersAPI.getMyHomeClasses().then(r => {
        if (r.data.success) {
          setHomeClasses(r.data.data);
          if (r.data.data.length > 0) setSelectedClass(r.data.data[0].id);
        }
      }).catch(() => {});
    }
  }, []);

  const loadScoresheet = async () => {
    if (!selectedYear || !selectedClass) return;
    setLoading(true); setError(''); setScoresheet(null);
    try {
      const res = await scoresheetAPI.getClass({ class_id: selectedClass, academic_year_id: selectedYear });
      if (res.data.success) setScoresheet(res.data.data);
      else setError(res.data.message || 'Failed to load scoresheet');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading scoresheet');
    } finally { setLoading(false); }
  };

  const printScoresheet = () => {
    const token = localStorage.getItem('sms_token');
    window.open(`${API}/reports/pdf/scoresheet?class_id=${selectedClass}&academic_year_id=${selectedYear}&token=${token}`, '_blank');
  };

  const downloadExcel = async () => {
    if (!scoresheet) return;
    // Build CSV matching the PDF format: Surname, Name, [subjects], AVG, POS, P/F, grade counts
    const subs = scoresheet.subjects || [];
    const passM = scoresheet.promotion_rule?.subject_pass_mark || 50;

    const headers = ['POS', 'Surname', 'Name', ...subs.map(s => s.code), 'AVG', 'P/F',
                     'A*', 'A', 'B', 'C', 'D', 'E', 'F', 'PASSED', 'CREDITS'];
    const rows = scoresheet.scoresheet.map(row => {
      const subScores = subs.map(s => {
        const d = row.subjects?.[s.id];
        return d?.percent !== null && d?.percent !== undefined ? d.percent.toFixed(1) : '';
      });
      const gc = row.grade_counts || {};
      const credits = (gc.A_star || 0) + (gc.A || 0) + (gc.B || 0) + (gc.C || 0);
      return [
        row.class_rank,
        row.last_name,
        row.first_name,
        ...subScores,
        row.aggregate !== null ? row.aggregate.toFixed(1) : '',
        row.passes ? 'P' : 'F',
        gc.A_star || 0, gc.A || 0, gc.B || 0, gc.C || 0, gc.D || 0, gc.E || 0, gc.F || 0,
        row.subjects_passed || 0,
        credits
      ];
    });

    // Class average footer
    const avgRow = ['CLASS AVG', '', '',
      ...subs.map(s => {
        const a = scoresheet.subject_class_averages?.[s.id];
        return a !== null && a !== undefined ? parseFloat(a).toFixed(1) : '';
      }),
      scoresheet.class_average !== null ? scoresheet.class_average.toFixed(1) : '',
      `${scoresheet.pass_rate}%`,
      '', '', '', '', '', '', '', '', ''
    ];

    const csv = [headers, ...rows, avgRow]
      .map(r => r.map(v => `"${v}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    const cls = classes.find(c => c.id == selectedClass);
    const yr  = academicYears.find(y => y.id == selectedYear);
    a.download = `Scoresheet_${cls?.name || 'Class'}_${yr?.year_label || ''}_${yr?.term || ''}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const yr  = academicYears.find(y => y.id == selectedYear);
  const cls = classes.find(c => c.id == selectedClass);
  const passM = scoresheet?.promotion_rule?.subject_pass_mark || 50;

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>Scoresheet</Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField select label="Academic Year" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Class" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
                <MenuItem value="">Select class…</MenuItem>
                {(isClassTeacher ? homeClasses : classes).map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="contained" onClick={loadScoresheet} disabled={!selectedYear || !selectedClass || loading}
                fullWidth startIcon={<Refresh />}
                sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' }, fontWeight: 700 }}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Load'}
              </Button>
            </Grid>
            {scoresheet && (
              <>
                <Grid item xs={6} sm={1}>
                  <Tooltip title="Print premium scoresheet">
                    <Button variant="outlined" onClick={printScoresheet} fullWidth size="small" startIcon={<Print />}>
                      Print
                    </Button>
                  </Tooltip>
                </Grid>
                <Grid item xs={6} sm={1}>
                  <Tooltip title="Download as CSV (open in Excel)">
                    <Button variant="outlined" onClick={downloadExcel} fullWidth size="small" startIcon={<Download />}>
                      CSV
                    </Button>
                  </Tooltip>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {scoresheet && (
        <>
          {/* Header info matching the PDF */}
          <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #0f2b4a 0%, #1a3a5c 100%)' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="h5" fontWeight={800} color="white" fontFamily="serif">
                    SCORE SHEET — TERM PERFORMANCE
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,.6)', mt: 0.5 }}>
                    {yr?.year_label} &nbsp;·&nbsp; {yr?.term} &nbsp;·&nbsp; {cls?.name}{cls?.stream ? ' ' + cls?.stream : ''}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>Total Students</Typography>
                    <Typography fontWeight={800} fontSize={28} color="white">{scoresheet.students_count}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>Class Avg</Typography>
                    <Typography fontWeight={800} fontSize={28} color="#c9952a">
                      {scoresheet.class_average !== null ? scoresheet.class_average.toFixed(1) : '—'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Stats row */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                {[
                  { label: 'Passed', value: scoresheet.total_passed, color: '#4ade80' },
                  { label: 'Failed', value: scoresheet.total_failed, color: '#f87171' },
                  { label: 'Pass Rate', value: `${scoresheet.pass_rate}%`, color: '#c9952a' },
                  { label: 'Subjects', value: scoresheet.subjects?.length, color: 'rgba(255,255,255,.8)' },
                ].map(s => (
                  <Box key={s.label} sx={{ textAlign: 'center', px: 2, borderRight: '1px solid rgba(255,255,255,.1)' }}>
                    <Typography fontSize={18} fontWeight={800} color={s.color}>{s.value}</Typography>
                    <Typography fontSize={9} color="rgba(255,255,255,.45)" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{s.label}</Typography>
                  </Box>
                ))}
                {scoresheet.promotion_rule && (
                  <Tooltip title={`Aggregate: ${scoresheet.promotion_rule.aggregate_method?.replace(/_/g,' ')} · Pass decision: ${scoresheet.promotion_rule.pass_decision?.replace(/_/g,' ')} · Subject pass mark: ${passM}%`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help', px: 2 }}>
                      <Info sx={{ fontSize: 14, color: 'rgba(255,255,255,.4)' }} />
                      <Typography fontSize={10} color="rgba(255,255,255,.5)">
                        Rule: {scoresheet.promotion_rule.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Main scoresheet table */}
          <Card>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  {/* Row 1: fixed cols + subject codes + summary */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#0f2b4a', color: 'white', width: 36, fontSize: 10, position: 'sticky', left: 0, zIndex: 3 }}>POS</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#0f2b4a', color: 'white', minWidth: 90, fontSize: 10, position: 'sticky', left: 36, zIndex: 3 }}>SURNAME</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#0f2b4a', color: 'white', minWidth: 80, fontSize: 10, position: 'sticky', left: 126, zIndex: 3 }}>NAME</TableCell>
                    {scoresheet.subjects?.map(sub => (
                      <TableCell key={sub.id} align="center"
                        sx={{ fontWeight: 800, bgcolor: sub.is_core ? '#0a1f35' : '#152f4a', color: sub.is_core ? '#c9952a' : 'rgba(255,255,255,.75)', minWidth: 52, fontSize: 9, p: '4px 2px' }}>
                        <Tooltip title={sub.name}>
                          <Box>
                            {sub.code}
                            {sub.is_core && <Box component="span" sx={{ display: 'block', fontSize: 7, color: '#c9952a', letterSpacing: 0.5 }}>CORE</Box>}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#0a1f35', color: '#c9952a', width: 56, fontSize: 10, borderLeft: '2px solid #c9952a' }}>AVG</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#0a1f35', color: '#c9952a', width: 44, fontSize: 10 }}>GRD</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#0a1f35', color: '#c9952a', width: 36, fontSize: 10 }}>P/F</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#0a1f35', color: 'rgba(255,255,255,.6)', width: 36, fontSize: 8 }}>A*+A</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#0a1f35', color: 'rgba(255,255,255,.6)', width: 36, fontSize: 8 }}>B+C</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#0a1f35', color: 'rgba(255,255,255,.6)', width: 36, fontSize: 8 }}>PASS</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {scoresheet.scoresheet?.map((row, idx) => {
                    const gc = row.grade_counts || {};
                    const distinctions = (gc.A_star || 0) + (gc.A || 0);
                    const credits = (gc.B || 0) + (gc.C || 0);
                    const rowBg = idx % 2 === 0 ? 'white' : '#f8f9fb';

                    return (
                      <TableRow key={row.student_id} hover sx={{ bgcolor: rowBg }}>
                        {/* Rank */}
                        <TableCell sx={{ fontWeight: 800, fontSize: 11, color: '#0f2b4a', position: 'sticky', left: 0, bgcolor: rowBg, zIndex: 1, textAlign: 'center', p: '3px 4px' }}>
                          <Box sx={{
                            width: 22, height: 22, borderRadius: '50%', mx: 'auto',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 9, fontWeight: 800,
                            bgcolor: row.class_rank === 1 ? '#ffd700' : row.class_rank === 2 ? '#c0c0c0' : row.class_rank === 3 ? '#cd7f32' : 'transparent',
                            color: row.class_rank <= 3 ? '#333' : '#555',
                            border: row.class_rank > 3 ? '1px solid #ddd' : 'none'
                          }}>
                            {row.class_rank}
                          </Box>
                        </TableCell>
                        {/* Surname */}
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, position: 'sticky', left: 36, bgcolor: rowBg, zIndex: 1, p: '3px 6px', whiteSpace: 'nowrap' }}>
                          {row.last_name}
                        </TableCell>
                        {/* First name */}
                        <TableCell sx={{ fontSize: 12, position: 'sticky', left: 126, bgcolor: rowBg, zIndex: 1, p: '3px 6px', whiteSpace: 'nowrap' }}>
                          {row.first_name}
                        </TableCell>

                        {/* Subject scores */}
                        {scoresheet.subjects?.map(sub => (
                          <SubjectCell key={sub.id} data={row.subjects?.[sub.id]} passMark={passM} />
                        ))}

                        {/* Aggregate */}
                        <TableCell align="center" sx={{ borderLeft: '2px solid rgba(201,149,42,0.4)', bgcolor: 'rgba(15,43,74,0.04)', p: '3px 4px' }}>
                          {row.aggregate !== null ? (
                            <Typography fontWeight={800} fontSize={12}
                              color={row.passes ? '#1a7a3c' : '#c0392b'}>
                              {row.aggregate.toFixed(1)}
                            </Typography>
                          ) : <Typography fontSize={11} color="#ccc">—</Typography>}
                        </TableCell>

                        {/* Grade */}
                        <TableCell align="center" sx={{ bgcolor: 'rgba(15,43,74,0.04)', p: '3px 4px' }}>
                          {row.overall_grade !== '-' ? (
                            <Box sx={{ bgcolor: gradeBg(row.overall_grade), color: 'white', borderRadius: 0.8, px: 0.8, fontSize: 10, fontWeight: 800, display: 'inline-block' }}>
                              {row.overall_grade}
                            </Box>
                          ) : <Typography fontSize={11} color="#ccc">—</Typography>}
                        </TableCell>

                        {/* Pass/Fail */}
                        <TableCell align="center" sx={{ bgcolor: 'rgba(15,43,74,0.04)', p: '3px 4px' }}>
                          <Box sx={{
                            display: 'inline-block',
                            fontSize: 10, fontWeight: 800,
                            px: 0.8, borderRadius: 0.8,
                            bgcolor: row.passes ? '#e6f4ec' : '#fdecea',
                            color: row.passes ? '#1a7a3c' : '#c0392b'
                          }}>
                            {row.passes ? 'P' : 'F'}
                          </Box>
                        </TableCell>

                        {/* Grade counts */}
                        <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, color: distinctions > 0 ? '#1a7a3c' : '#aaa', p: '3px 4px' }}>
                          {distinctions || '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, color: credits > 0 ? '#1565c0' : '#aaa', p: '3px 4px' }}>
                          {credits || '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, color: row.subjects_passed > 0 ? '#2e7d32' : '#aaa', p: '3px 4px' }}>
                          {row.subjects_passed || 0}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Class average footer row */}
                  <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                    <TableCell colSpan={3} sx={{ color: '#c9952a', fontWeight: 800, fontSize: 11, position: 'sticky', left: 0, bgcolor: '#0f2b4a', zIndex: 1, p: '6px 8px' }}>
                      CLASS AVERAGE
                    </TableCell>
                    {scoresheet.subjects?.map(sub => {
                      const avg = scoresheet.subject_class_averages?.[sub.id];
                      return (
                        <TableCell key={sub.id} align="center" sx={{ color: avg !== null ? (avg >= passM ? '#4ade80' : '#f87171') : '#666', fontWeight: 700, fontSize: 11, p: '6px 3px' }}>
                          {avg !== null && avg !== undefined ? parseFloat(avg).toFixed(1) : '—'}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ color: '#c9952a', fontWeight: 800, fontSize: 13, borderLeft: '2px solid rgba(201,149,42,.5)', p: '6px 4px' }}>
                      {scoresheet.class_average !== null ? scoresheet.class_average.toFixed(1) : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'rgba(255,255,255,.5)', fontSize: 10, p: '6px 4px' }}>—</TableCell>
                    <TableCell align="center" sx={{ color: '#c9952a', fontWeight: 800, fontSize: 11, p: '6px 4px' }}>
                      {scoresheet.pass_rate}%
                    </TableCell>
                    <TableCell colSpan={3} sx={{ color: 'rgba(255,255,255,.4)', fontSize: 10, p: '6px 4px' }}>
                      {scoresheet.total_passed} passed · {scoresheet.total_failed} failed
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>Grade Key:</Typography>
            {[
              { grade: 'A*', label: '≥90', bg: '#1a5c2e' },
              { grade: 'A',  label: '80–89', bg: '#1a7a3c' },
              { grade: 'B',  label: '70–79', bg: '#1565c0' },
              { grade: 'C',  label: '60–69', bg: '#2e7d32' },
              { grade: 'D',  label: '50–59', bg: '#b7770d' },
              { grade: 'E',  label: '40–49', bg: '#e67e22' },
              { grade: 'F',  label: '<40',   bg: '#c0392b' },
            ].map(g => (
              <Box key={g.grade} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ bgcolor: g.bg, color: 'white', px: 0.8, borderRadius: 0.8, fontSize: 10, fontWeight: 800 }}>{g.grade}</Box>
                <Typography fontSize={10} color="text.secondary">{g.label}</Typography>
              </Box>
            ))}
            <Divider orientation="vertical" flexItem />
            <Typography fontSize={10} color="text.secondary">
              Red score = below {passM}% (subject pass mark)
            </Typography>
            {!scoresheet.promotion_rule && (
              <Alert severity="warning" sx={{ py: 0, ml: 'auto', fontSize: 11 }}>
                No promotion rule set — using default (avg ≥ 50%)
              </Alert>
            )}
          </Box>
        </>
      )}

      {!scoresheet && !loading && (
        <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
          <Typography variant="h6" color="text.disabled">Select an academic year and class, then click Load</Typography>
        </Box>
      )}
    </Box>
  );
}
